import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, BuildingIcon, CalendarIcon, SparklesIcon } from 'lucide-react';
export interface TripDetails {
  destination: string;
  resort: string;
  departureDate: string;
  returnDate: string;
}
interface TripFormProps {
  onSubmit: (details: TripDetails) => void;
  isLoading: boolean;
}
export function TripForm({
  onSubmit,
  isLoading
}: TripFormProps) {
  const [formData, setFormData] = useState<TripDetails>({
    destination: '',
    resort: '',
    departureDate: '',
    returnDate: ''
  });
  const [errors, setErrors] = useState<Partial<TripDetails>>({});
  const validate = () => {
    const newErrors: Partial<TripDetails> = {};
    if (!formData.destination.trim()) newErrors.destination = 'Destination is required';
    if (!formData.resort.trim()) newErrors.resort = 'Resort name is required';
    if (!formData.departureDate) newErrors.departureDate = 'Departure date is required';
    if (!formData.returnDate) newErrors.returnDate = 'Return date is required';
    if (formData.departureDate && formData.returnDate) {
      if (new Date(formData.returnDate) < new Date(formData.departureDate)) {
        newErrors.returnDate = 'Return date must be after departure';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof TripDetails]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  const inputBase = (hasError: boolean) => `block w-full pl-11 pr-4 py-3.5 border ${hasError ? 'border-red-400/60 focus:ring-red-400 focus:border-red-400' : 'border-gold-500/20 focus:ring-gold-400 focus:border-gold-400'} rounded bg-black/40 text-white placeholder-white/30 focus:outline-none focus:ring-1 transition-all duration-300 sm:text-sm backdrop-blur-sm`;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    delay: 0.2,
    ease: [0.22, 1, 0.36, 1]
  }} className="w-full max-w-3xl mx-auto relative" data-id="element-114">
      {/* Main card */}
      <div className="relative bg-gradient-to-b from-navy-900 via-[#0d1f3c] to-navy-900 p-10 md:p-12 rounded border border-gold-500/20 shadow-2xl overflow-hidden" data-id="element-115">
        {/* Subtle radial glow behind the card */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(201,169,110,0.06)_0%,_transparent_70%)]" data-id="element-116" />

        {/* Corner ornaments */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-gold-400/60 rounded-tl" data-id="element-117" />
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-gold-400/60 rounded-tr" data-id="element-118" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-gold-400/60 rounded-bl" data-id="element-119" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-gold-400/60 rounded-br" data-id="element-120" />

        {/* Subtle dot pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(#c9a96e 0.5px, transparent 0.5px)',
        backgroundSize: '24px 24px'
      }} data-id="element-121" />

        <div className="relative z-10" data-id="element-122">
          {/* Header */}
          <div className="text-center mb-10" data-id="element-123">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-124">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: 40
            }} transition={{
              duration: 0.8,
              delay: 0.5
            }} className="h-px bg-gradient-to-r from-transparent to-gold-400" data-id="element-125" />
              <SparklesIcon className="w-4 h-4 text-gold-400" data-id="element-126" />
              <motion.div initial={{
              width: 0
            }} animate={{
              width: 40
            }} transition={{
              duration: 0.8,
              delay: 0.5
            }} className="h-px bg-gradient-to-l from-transparent to-gold-400" data-id="element-127" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-3 tracking-wide" data-id="element-128">
              Design Your Escape
            </h2>
            <p className="text-gold-400/70 text-sm tracking-widest uppercase font-light" data-id="element-129">
              Provide your travel details for a bespoke itinerary
            </p>
            <div className="flex items-center justify-center gap-2 mt-6" data-id="element-130">
              <div className="w-12 h-px bg-gold-500/30" data-id="element-131" />
              <div className="w-1.5 h-1.5 rounded-full bg-gold-400/60" data-id="element-132" />
              <div className="w-12 h-px bg-gold-500/30" data-id="element-133" />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-7" data-id="element-134">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-135">
              {/* Destination */}
              <div className="space-y-2.5" data-id="element-136">
                <label htmlFor="destination" className="block text-xs font-medium text-gold-400/80 tracking-widest uppercase" data-id="element-137">
                  Destination City or Country
                </label>
                <div className="relative group" data-id="element-138">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-139">
                    <MapPinIcon className="h-4 w-4 text-gold-400/50 group-focus-within:text-gold-400 transition-colors" data-id="element-140" />
                  </div>
                  <input type="text" id="destination" name="destination" value={formData.destination} onChange={handleChange} className={inputBase(!!errors.destination)} placeholder="e.g., Amalfi Coast, Italy" disabled={isLoading} data-id="element-141" />
                </div>
                {errors.destination && <p className="text-red-400 text-xs" data-id="element-142">{errors.destination}</p>}
              </div>

              {/* Resort */}
              <div className="space-y-2.5" data-id="element-143">
                <label htmlFor="resort" className="block text-xs font-medium text-gold-400/80 tracking-widest uppercase" data-id="element-144">
                  Resort or Hotel Name
                </label>
                <div className="relative group" data-id="element-145">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-146">
                    <BuildingIcon className="h-4 w-4 text-gold-400/50 group-focus-within:text-gold-400 transition-colors" data-id="element-147" />
                  </div>
                  <input type="text" id="resort" name="resort" value={formData.resort} onChange={handleChange} className={inputBase(!!errors.resort)} placeholder="e.g., Le Sirenuse" disabled={isLoading} data-id="element-148" />
                </div>
                {errors.resort && <p className="text-red-400 text-xs" data-id="element-149">{errors.resort}</p>}
              </div>

              {/* Departure Date */}
              <div className="space-y-2.5" data-id="element-150">
                <label htmlFor="departureDate" className="block text-xs font-medium text-gold-400/80 tracking-widest uppercase" data-id="element-151">
                  Departure Date
                </label>
                <div className="relative group" data-id="element-152">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-153">
                    <CalendarIcon className="h-4 w-4 text-gold-400/50 group-focus-within:text-gold-400 transition-colors" data-id="element-154" />
                  </div>
                  <input type="date" id="departureDate" name="departureDate" value={formData.departureDate} onChange={handleChange} className={inputBase(!!errors.departureDate)} disabled={isLoading} data-id="element-155" />
                </div>
                {errors.departureDate && <p className="text-red-400 text-xs" data-id="element-156">{errors.departureDate}</p>}
              </div>

              {/* Return Date */}
              <div className="space-y-2.5" data-id="element-157">
                <label htmlFor="returnDate" className="block text-xs font-medium text-gold-400/80 tracking-widest uppercase" data-id="element-158">
                  Return Date
                </label>
                <div className="relative group" data-id="element-159">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-160">
                    <CalendarIcon className="h-4 w-4 text-gold-400/50 group-focus-within:text-gold-400 transition-colors" data-id="element-161" />
                  </div>
                  <input type="date" id="returnDate" name="returnDate" value={formData.returnDate} onChange={handleChange} className={inputBase(!!errors.returnDate)} disabled={isLoading} data-id="element-162" />
                </div>
                {errors.returnDate && <p className="text-red-400 text-xs" data-id="element-163">{errors.returnDate}</p>}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4" data-id="element-164">
              <button type="submit" disabled={isLoading} className="w-full relative py-4 px-6 rounded border border-gold-400/40 text-sm font-medium tracking-widest uppercase transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden group bg-gradient-to-r from-gold-500 to-gold-400 text-navy-900 hover:from-gold-400 hover:to-gold-500 hover:shadow-[0_0_30px_rgba(201,169,110,0.3)]" data-id="element-165">
                <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-166">
                  {isLoading ? <>
                      <motion.div animate={{
                    rotate: 360
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="w-4 h-4 border-2 border-navy-900/30 border-t-navy-900 rounded-full" data-id="element-167" />
                      Curating Your Journey...
                    </> : 'Discover Recommendations'}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>;
}